import { createWorker, OEM } from 'tesseract.js';
import { Document, Packer, Paragraph, TextRun } from 'docx';
import { mountImageBatch } from './image-batch-queue.js';
import { imageIsAr, imageName, openBitmap } from './image-batch-utils.js';
import { trackEvent } from './extra-common.js';

const t = (ar, en) => imageIsAr ? ar : en;
let worker = null; let workerLanguage = ''; let activeReport = () => {};
async function getWorker(language, report) {
  if (worker && workerLanguage !== language) { await worker.terminate(); worker = null; }
  if (!worker) {
    workerLanguage = language;
    activeReport = report;
    worker = await createWorker(language, OEM.LSTM_ONLY, {
      workerPath: '/assets/vendor/tesseract/worker.min.js', corePath: '/assets/vendor/tesseract-core/',
      langPath: '/assets/vendor/tessdata/', workerBlobURL: false, gzip: true, cacheMethod: 'write',
      logger: (event) => {
        if (typeof event.progress === 'number') activeReport(Math.min(90, 8 + event.progress * 82), event.status || t('جارٍ قراءة النص', 'Recognizing text'));
        else activeReport(8, event.status || t('جارٍ تحميل محرك OCR', 'Loading OCR engine'));
      }
    });
  }
  activeReport = report;
  return worker;
}

async function textToDocx(text) {
  const paragraphs = text.split('\n').map((line) => new Paragraph({
    children: [new TextRun({ text: line, size: 24, font: 'Calibri' })]
  }));
  const doc = new Document({
    sections: [{
      properties: {},
      children: paragraphs
    }]
  });
  return await Packer.toBlob(doc);
}

mountImageBatch({
  rootId: 'ocr-reader-app',
  fields: [
    {
      key: 'language',
      label: t('لغة النص', 'Text language'),
      type: 'select',
      default: 'ara+eng',
      options: [
        { value: 'ara+eng', label: t('العربية والإنجليزية', 'Arabic + English') },
        { value: 'ara', label: t('العربية', 'Arabic') },
        { value: 'eng', label: t('الإنجليزية', 'English') }
      ]
    },
    {
      key: 'format',
      label: t('صيغة التصدير', 'Export format'),
      type: 'select',
      default: 'txt',
      options: [
        { value: 'txt', label: t('ملف نصي (TXT)', 'Text File (.txt)') },
        { value: 'docx', label: t('مستند وورد (Word .docx)', 'Word Document (.docx)') }
      ]
    }
  ],
  zipName: 'recognized-text-files.zip', process: async (file, settings, report) => {
    trackEvent('tool_started', 'ocr-reader');
    try {
      const bitmap = await openBitmap(file); bitmap.close();
      report(4, t('تهيئة محرك التعرف المحلي', 'Preparing the local recognition engine'));
      const engine = await getWorker(settings.language, report);
      const { data } = await engine.recognize(file);
      const text = data.text || '';
      if (!text.trim()) throw new Error(t('لم يُعثر على نص واضح في الصورة.', 'No clear text was found in this image.'));
      report(92, t('تجهيز ملف التصدير', 'Preparing output file'));
      const isDocx = settings.format === 'docx';
      const blob = isDocx ? await textToDocx(text) : new Blob([text], { type: 'text/plain;charset=utf-8' });
      const ext = isDocx ? 'docx' : 'txt';
      report(100, t('اكتمل استخراج النص', 'Text extraction complete'));
      trackEvent('tool_completed', 'ocr-reader');
      return { blob, filename: `${imageName(file.name)}.${ext}`, details: `${t('دقة تقديرية', 'Estimated confidence')}: ${Math.round(data.confidence)}%\n${text.slice(0, 900)}` };
    } catch (err) {
      trackEvent('tool_failed', 'ocr-reader');
      throw err;
    }
  }
});
window.addEventListener('pagehide', () => { worker?.terminate(); worker = null; });
